import createMiddleware from 'next-intl/middleware';
import { NextRequest, NextResponse } from 'next/server';

import { routing } from '@flipster/i18n/routing';
import { oauthProviderSearchParamKey } from '@flipster/utils/constants/oauth';
import { removeLocalePathname } from '@flipster/utils/pathname';

const handleI18nRouting = createMiddleware(routing);

/**
 * next-intl 로케일 협상에서 `en-GB`, `en-SG` 등이 `en-AE`로 매칭되는 것을 피하기 위해,
 * UAE(`en-AE`)가 아닌 지역 영어 태그는 기본 로케일 `en`(경로 `/`)으로 맞춥니다.
 */
function normalizeEnglishAcceptLanguageSegment(part: string): string {
  const s = part.trim();
  const i = s.indexOf(';');
  const tag = (i === -1 ? s : s.slice(0, i)).trim().toLowerCase();
  const tail = i === -1 ? '' : s.slice(i);

  if (tag !== 'en' && tag !== 'en-ae' && tag.startsWith('en-')) return `en${tail}`;
  return s;
}

function normalizeEnglishRegionalVariantsToDefault(header: string): string {
  return header.split(',').map(normalizeEnglishAcceptLanguageSegment).join(',');
}

/**
 * UAE IP에서 접속한 사용자는 브라우저 언어 설정과 무관하게 `en-AE` 컨텐츠를 보도록 강제합니다.
 * 그 외의 경우엔 `en-*` 지역 변형을 기본 `en`으로 정규화합니다.
 * 둘 다 명시적인 로케일 경로(`/vi/...` 등)는 next-intl이 경로를 우선시하므로 영향 없음.
 */
function withNormalizedAcceptLanguage(request: NextRequest): NextRequest {
  const raw = request.headers.get('accept-language');
  const cc = request.headers.get('x-vercel-ip-country');

  const fixed = cc === 'AE' ? 'en-AE' : raw ? normalizeEnglishRegionalVariantsToDefault(raw) : null;

  if (fixed === null || fixed === raw) return request;

  const headers = new Headers(request.headers);
  headers.set('accept-language', fixed);

  return new NextRequest(request.url, {
    headers,
    method: request.method,
    body: request.body,
  });
}

export const SignInPath = [
  '/signin',
  '/login',
  '/signup',
  '/email-signup',
  '/social-signup',
  '/forgotPassword',
  '/resetPassword',
  '/select-nationality',
];

export const RequiredLoggedInPath = [
  '/ab-test',
  '/assets',
  '/user',
  '/history',
  '/support/referral',
  '/support/affiliate',
  '/oauth',
  '/pnl-analysis',
  '/copy-trading/activity',
  '/support/proof-of-reserves/audit',
  '/usd1-points',
  '/travel-rule/proof',
];

const isValidInternalPath = (path: string): boolean => {
  return path.startsWith('/') && !path.startsWith('//') && !path.includes('://');
};

// X OAuth PKCE 콜백(features/src/profile-page/modal/edit-profile/shared/oauth.ts)은
// x.com에서 돌아오는 cross-site top-level 이동이라 SameSite=Strict 세션 쿠키(session_id_nuts)가
// 실려오지 않는다. 이 요청을 로그인 가드로 /login에 보내면 code가 유실되어 연동이 실패하므로(QA-21195),
// 콜백 파라미터(oauth-provider+code+state)가 모두 있으면 가드를 통과시켜 클라이언트가 처리하게 한다.
const isOAuthCallback = (searchParams: URLSearchParams): boolean =>
  searchParams.has(oauthProviderSearchParamKey) && searchParams.has('code') && searchParams.has('state');

export default function proxy(request: NextRequest) {
  if (request.method === 'POST' && request.url.includes('/[locale]')) {
    return new NextResponse('Aborted by middleware', { status: 403 });
  }

  // Auth redirect
  const nuts = request.cookies.get('session_id_nuts');
  const isLoggedIn = !!nuts?.value;
  const pathname = removeLocalePathname(request.nextUrl.pathname);

  if (!isLoggedIn) {
    const isRequiredLoggedIn = RequiredLoggedInPath.some((path) => {
      const regex = new RegExp(`^${path}($|/)`);
      return regex.test(pathname);
    });
    if (isRequiredLoggedIn && !isOAuthCallback(request.nextUrl.searchParams)) {
      const response = NextResponse.redirect(new URL('/login', request.url));
      // align with getCookieNutsDomain
      const hostname = request.nextUrl.hostname;
      const domain = hostname.endsWith('localhost')
        ? hostname
        : hostname.endsWith('.yoda.mobi')
          ? '.yoda.mobi'
          : `.${hostname}`;
      response.cookies.set('referral_path', request.nextUrl.pathname, { path: '/', domain });
      return response;
    }
  }

  if (isLoggedIn) {
    const isSignInPath = SignInPath.some((path) => pathname.startsWith(path));
    if (isSignInPath) {
      const referralPath = request.cookies.get('referral_path')?.value;
      const target = referralPath && isValidInternalPath(referralPath) ? removeLocalePathname(referralPath) : '/';
      const redirectTo = target === '/' ? '/assets' : target;
      return NextResponse.redirect(new URL(redirectTo, request.url));
    }
  }

  // i18n routing
  const response = handleI18nRouting(withNormalizedAcceptLanguage(request));

  const [, requestLocale] = new URL(request.url).pathname.split('/');

  const location = response.headers.get('Location');
  if (response.status === 307 && requestLocale === 'en' && location != null) {
    return NextResponse.redirect(new URL(location), {
      headers: response.headers,
      status: 308,
    });
  }

  return response;
}

export const config = {
  matcher: [
    // api and static files, assets (including sitemap, robots) don't have localized version.
    '/((?!api|_next|_vercel|.*\\..*).*)',
    // symbol can contain dot characters -- you have to check before adding dynamic routes.
    '/([\\w-]+)?/(trade|fullchart|support)/(.+)',
  ],
};
